import {
    IconAlertCircle,
    IconArrowUp,
    IconChevronDown,
    IconChevronRight,
    IconCornerRightUpDouble,
    IconLibraryMinus,
    IconMinus,
    IconPlus,
    IconStethoscope,
} from '@tabler/icons-react'
import uniqBy from 'lodash/uniqBy'
import { useState } from 'react'
import { SetNonNullable, SetRequired } from 'type-fest'

import { AllowedIcon, AstraButton, AstraIcon, AstraParagraph, AstraPopover, AstraTooltip } from '~/astra'
import { SurgeryTypeGroupEnum } from '~/clients/derived-api-types'
import { RuleValueChipWrapper } from '~/components'
import { SurgeryTypeGroupComboBox } from '~/components/BlockScheduleForm/shared/components/SurgeryTypeGroupComboBox'
import { RuleValuesChip } from '~/components/RuleValuesChip'
import { type ResolvedSurgeryTypeGroup, useGetResolvedSurgeryTypeGroup } from '~/storm'

import { type FormError, type FormProfileSurgeryTypeGroup, isSurgeryTypeGroupError } from './formState'

export type MinimalSurgeryTypeGroup = {
    id: number
    code: string
    display_name: string
    group_type: SurgeryTypeGroupEnum
}
type GroupedByLevel = {
    parent: MinimalSurgeryTypeGroup
    children: MinimalSurgeryTypeGroup[]
}

const SurgeryGroupItem = ({
    surgeryTypeGroup,
    onRemove,
    error,
    onMoveClick,
}: {
    surgeryTypeGroup: MinimalSurgeryTypeGroup
    onRemove: (code: string) => void
    error?: FormError
    onMoveClick?: () => void
}) => (
    <div className="flex w-full flex-1 gap-8 pl-[22px]">
        <div className="flex flex-grow flex-col">
            <RuleValueChipWrapper resolved={{ groupLevel: surgeryTypeGroup.group_type, surgeryTypeGroupLabel: surgeryTypeGroup.display_name }} fullWidth>
                <RuleValuesChip
                    variant="filled"
                    text={surgeryTypeGroup.display_name}
                    groupLevel={surgeryTypeGroup.group_type}
                    fullWidth
                    color={error ? 'critical' : 'default'}
                    endIcon={
                        error && (
                            <AstraTooltip tooltipContent={error.message}>
                                <AstraIcon icon={IconAlertCircle} />
                            </AstraTooltip>
                        )
                    }
                />
            </RuleValueChipWrapper>
        </div>
        <div className="flex items-center gap-1">
            {onMoveClick && (
                <AstraTooltip tooltipContent="Flytt til hovedoperatør">
                    <AstraButton variant="ghost" size="medium" icon={<AstraIcon icon={IconArrowUp} />} type="button" onClick={onMoveClick} />
                </AstraTooltip>
            )}
            <AstraButton variant="ghost" size="medium" icon={<AstraIcon icon={IconMinus} />} type="button" onClick={() => onRemove(surgeryTypeGroup.code)} />
        </div>
    </div>
)
function CollapsibleSurgeryTypeGroup({
    parent,
    items,
    onRemove,
    onMoveClick,
    errors,
}: {
    parent: MinimalSurgeryTypeGroup
    items: MinimalSurgeryTypeGroup[]
    onRemove: (code: string) => void
    onMoveClick?: (items: MinimalSurgeryTypeGroup[]) => void
    errors?: Array<FormError>
}) {
    const [isOpen, setIsOpen] = useState(true)

    const getError = (item: MinimalSurgeryTypeGroup) => errors?.find(e => isSurgeryTypeGroupError(e) && e.params.code === item.code)

    const hasAnyError = getError(parent) || items.some(item => !!getError(item))

    // if no children, just treat it as a single item
    if (items.length === 0) {
        return <SurgeryGroupItem surgeryTypeGroup={parent} onRemove={onRemove} error={getError(parent)} />
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
                <button type="button" className={'flex items-center gap-2'} onClick={() => setIsOpen(prev => !prev)}>
                    <AstraIcon icon={isOpen ? IconChevronDown : IconChevronRight} size="small" color="text-muted" />
                    <RuleValuesChip
                        variant="outlined"
                        text={parent.display_name}
                        groupLevel={parent.group_type}
                        color={!isOpen && hasAnyError ? 'critical' : 'default'}
                    />
                </button>
                {isOpen && (
                    <div className="flex items-center gap-1">
                        {onMoveClick && (
                            <AstraTooltip tooltipContent="Flytt alle i gruppen til hovedoperatør">
                                <AstraButton
                                    variant="ghost"
                                    size="medium"
                                    icon={<AstraIcon icon={IconCornerRightUpDouble} />}
                                    type="button"
                                    onClick={() => onMoveClick(items)}
                                />
                            </AstraTooltip>
                        )}
                        <AstraButton
                            variant="ghost"
                            size="medium"
                            icon={<AstraIcon icon={IconLibraryMinus} />}
                            type="button"
                            onClick={() => onRemove(parent.code)}
                        />
                    </div>
                )}
            </div>

            {isOpen && (
                <div className="flex flex-col gap-1 pl-4">
                    {items.map(child => (
                        <SurgeryGroupItem
                            key={child.code}
                            surgeryTypeGroup={child}
                            onRemove={onRemove}
                            error={getError(child)}
                            onMoveClick={onMoveClick ? () => onMoveClick([child]) : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function AddSurgeryTypePopover({ disabledOptions, onSelect }: { disabledOptions: string[]; onSelect: (code: string) => void }) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <AstraPopover
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            noPadding
            popoverContent={
                <SurgeryTypeGroupComboBox
                    value={null}
                    disabledOptions={disabledOptions}
                    onChange={option => {
                        if (option) {
                            onSelect(option)
                            setIsOpen(false)
                        }
                    }}
                />
            }
        >
            <AstraButton variant="secondary" size="medium" icon={<AstraIcon icon={IconPlus} />} type="button" />
        </AstraPopover>
    )
}

type ResolvedSurgeryTypeGroupWithFlatChildren = ResolvedSurgeryTypeGroup & {
    flatAllChildren: ResolvedSurgeryTypeGroup[]
}

const groupByFirstAncestor = (surgeryTypesToGroup: string[], relevantGroups: ResolvedSurgeryTypeGroupWithFlatChildren[]) => {
    const surgeryTypesToProcess = new Set(surgeryTypesToGroup)

    // use the first encountered group that has the surgery type in it's hierarchy
    // currently it seems that groups from selectGetSurgeryTypeGroup are in the order of the hierarchy, so we will get the "first ancestor".
    // but this is not necessarily guaranteed - but good enough for now, does not seem trivial to find the lowest ancestor right now.
    const groups: GroupedByLevel[] = relevantGroups.flatMap(g => {
        const selectedTypesInGroup = g.flatAllChildren
            .filter(c => {
                if (!surgeryTypesToProcess.has(c.code)) return false
                surgeryTypesToProcess.delete(c.code)
                return true
            })
            .toSorted((a, b) => a.display_name.localeCompare(b.display_name))

        if (selectedTypesInGroup.length > 0) {
            return [{ parent: g, children: selectedTypesInGroup }]
        }
        return []
    })
    return { groups, missingGroups: Array.from(surgeryTypesToProcess) }
}

const useGroupSurgeryTypes = (surgeryTypeGroupCodes: string[], levelToGroupBy: SurgeryTypeGroupEnum = 'subspeciality') => {
    const getSurgeryTypeGroup = useGetResolvedSurgeryTypeGroup()

    const relevantGroups: ResolvedSurgeryTypeGroupWithFlatChildren[] = getSurgeryTypeGroup.all
        .filter(g => g.group_type === levelToGroupBy)
        .map(g => ({ ...g, flatAllChildren: getSurgeryTypeGroup.flatSurgeryCategoriesByCode(g.code) ?? [] }))

    const { groups, missingGroups } = groupByFirstAncestor(surgeryTypeGroupCodes, relevantGroups)

    // if we cannot find a group - treat as its own group
    const missingTypes = missingGroups
        .map(g => ({ parent: getSurgeryTypeGroup.byCode(g), children: [] }))
        .filter((p): p is SetNonNullable<typeof p, 'parent'> => p.parent !== null)

    return groups.concat(missingTypes).toSorted((a, b) => a.parent.display_name.localeCompare(b.parent.display_name))
}

export type SurgeryTypeFormSelectProps = {
    value: FormProfileSurgeryTypeGroup[]
    levelToGroupBy?: SurgeryTypeGroupEnum
    onChange: (value: FormProfileSurgeryTypeGroup[]) => void
    label: string
    icon?: AllowedIcon
    errors?: Array<FormError>
    /**
     * If present, will show an arrow button next to selected surgeryTypeGroups.
     * This can be used to eg. move a surgeryTypeGroup from one group to another.
     */
    onMove?: (value: FormProfileSurgeryTypeGroup[]) => void
}

export function SurgeryTypeFormSelect({ value, levelToGroupBy, onChange, label, icon = IconStethoscope, errors, onMove }: SurgeryTypeFormSelectProps) {
    const getSurgeryTypeGroup = useGetResolvedSurgeryTypeGroup()
    const surgeryTypeGroupCodes = value.map(s => s.surgeryTypeGroup.code)

    const grouped = useGroupSurgeryTypes(surgeryTypeGroupCodes, levelToGroupBy)

    const handleAdd = (code: string) => {
        const group = getSurgeryTypeGroup.byCode(code)
        if (!group) return

        const allChildrenCategorySurgeryTypes = getSurgeryTypeGroup
            .flatSurgeryCategoriesByCode(code)
            ?.filter((c): c is SetRequired<typeof c, 'code'> => !!c.code && c.group_type === 'category')

        const itemsToAdd = allChildrenCategorySurgeryTypes?.map(c => ({ surgeryTypeGroup: { id: c.id, code: c.code }, id: undefined })) ?? []

        const newItems = uniqBy([...value, ...itemsToAdd], s => s.surgeryTypeGroup.code)
        onChange(newItems)
    }
    const handleRemove = (code: string) => {
        const codesToRemove = new Set(getSurgeryTypeGroup.flatSurgeryCategoriesByCode(code)?.map(c => c.code) ?? [])
        onChange(value.filter(s => !codesToRemove.has(s.surgeryTypeGroup.code)))
    }

    const handleMoveClick = (items: MinimalSurgeryTypeGroup[]) => {
        if (!onMove) return
        const movedItems = items.map(s => ({ surgeryTypeGroup: { id: s.id, code: s.code, displayName: s.display_name }, id: undefined }))
        onMove(movedItems)
    }

    return (
        <div className="w-full rounded-lg bg-surface-base">
            <div className="flex items-center justify-between rounded-lg bg-neutral-subtle-rest px-3 py-1">
                <div className="flex items-center gap-2">
                    <AstraIcon icon={icon} size="large" color="text-magic" backgroundColor="bg-brand-subtle-rest" backgroundShape="rounded" />
                    <AstraParagraph variant="body" weight="medium" className="text-default">
                        {label}
                    </AstraParagraph>
                </div>
                <AddSurgeryTypePopover disabledOptions={surgeryTypeGroupCodes} onSelect={handleAdd} />
            </div>
            <div className="flex flex-1 flex-col gap-2 p-4">
                {grouped.length > 0 ? (
                    grouped.map(group => (
                        <CollapsibleSurgeryTypeGroup
                            key={group.parent.code}
                            errors={errors}
                            parent={group.parent}
                            items={group.children}
                            onRemove={handleRemove}
                            onMoveClick={onMove ? handleMoveClick : undefined}
                        />
                    ))
                ) : (
                    <AstraParagraph variant="body-small" className="text-muted">
                        Ingen operasjonstyper spesifisert.
                    </AstraParagraph>
                )}
            </div>
        </div>
    )
}
